import React, { useEffect, useMemo, useState } from 'react';
import DonutLargeRoundedIcon from '@mui/icons-material/DonutLargeRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UnifiedPortfolioApp from './UnifiedPortfolioApp';
import { usePortfolioHeaderSelector } from './selectors/portfolioHeaderSelector';
import { usePortfolioStore } from './stores/portfolioStore';
import { useMacroMarketStore } from './stores/macroMarketStore';
import { formatSyncDate } from './utils/syncDate';

const STABLECOIN_SYMBOLS = ['USDT', 'USDC', 'BSC-USD', 'DAI', 'FDUSD', 'TUSD', 'BUSD', 'EURT', 'PAX', 'GUSD'];

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function formatUsd(value, maximumFractionDigits = 0) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return `$${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits })}`;
}

function formatSignedUsd(value, maximumFractionDigits = 0) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return `${value >= 0 ? '+' : '-'}${formatUsd(Math.abs(value), maximumFractionDigits)}`;
}

function formatSignedPercent(value, maximumFractionDigits = 1) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return `${value >= 0 ? '+' : ''}${value.toFixed(maximumFractionDigits)}%`;
}

function formatShare(value, maximumFractionDigits = 0) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0%';
  }
  return `${(value * 100).toFixed(maximumFractionDigits)}%`;
}

function coerceNumber(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function normalizeExposureValue(value) {
  const parsed = coerceNumber(value);
  if (parsed === null) {
    return null;
  }
  if (parsed > 1) {
    return clamp(parsed / 100);
  }
  return clamp(parsed);
}

function normalizeExposureSnapshot(exposure) {
  if (!exposure) {
    return null;
  }

  return {
    btc: normalizeExposureValue(exposure.btc) ?? 0,
    eth: normalizeExposureValue(exposure.eth) ?? 0,
    alt: normalizeExposureValue(exposure.alt) ?? 0,
    stable: normalizeExposureValue(exposure.stable) ?? 0,
  };
}

function sumExposureValues(exposure) {
  if (!exposure) {
    return 0;
  }

  return ['btc', 'eth', 'alt', 'stable'].reduce((total, key) => {
    const value = exposure[key];
    return total + (typeof value === 'number' && !Number.isNaN(value) ? value : 0);
  }, 0);
}

function deriveMarketRegime(rawRegime, marketBtcDominance, marketEthDominance) {
  if (rawRegime) {
    return rawRegime;
  }

  if (typeof marketBtcDominance === 'number') {
    if (marketBtcDominance > 55) {
      return 'BTC_DOMINANCE';
    }
    if (marketBtcDominance < 45) {
      return 'ALT_ROTATION';
    }
  }

  if (typeof marketEthDominance === 'number' && marketEthDominance > 18) {
    return 'ETH_ROTATION';
  }

  return 'NEUTRAL';
}

function humanizeMarketRegime(regime) {
  const normalized = String(regime || '').toUpperCase();

  if (normalized.includes('ALT')) {
    return 'rotation altcoins';
  }
  if (normalized.includes('ETH')) {
    return 'rotation ETH';
  }
  if (normalized.includes('BTC') || normalized.includes('BITCOIN')) {
    return 'dominance BTC';
  }
  return 'régime neutre';
}

function buildAllocationFromPositions(positions) {
  const runtimePriceBySymbol = new Map();

  if (typeof window !== 'undefined' && Array.isArray(window.cryptoTable)) {
    window.cryptoTable.forEach((entry) => {
      const symbol = typeof entry?.symbol === 'string' ? entry.symbol.toUpperCase() : null;
      const price = typeof entry?.priceUsd === 'number'
        ? entry.priceUsd
        : (typeof entry?.currentPrice === 'number' ? entry.currentPrice : null);

      if (symbol && typeof price === 'number' && !Number.isNaN(price)) {
        runtimePriceBySymbol.set(symbol, price);
      }
    });
  }

  const isPositiveNumber = (value) => typeof value === 'number' && !Number.isNaN(value) && value > 0;

  const aggregate = new Map();
  let totalValue = 0;

  (Array.isArray(positions) ? positions : []).forEach((position) => {
    const assetId = typeof position?.assetId === 'string' ? position.assetId.toUpperCase() : null;
    if (!assetId) {
      return;
    }

    const fallbackPrice = runtimePriceBySymbol.get(assetId);
    const sourceType = String(position?.sourceType || position?.type || '').toUpperCase();
    const storedPrice = isPositiveNumber(position?.price) ? position.price : null;
    const usedPrice = sourceType === 'MANUAL'
      ? (storedPrice ?? fallbackPrice)
      : (fallbackPrice ?? storedPrice);
    const rawValue = typeof usedPrice === 'number' && typeof position?.quantity === 'number' && !Number.isNaN(position.quantity)
      ? usedPrice * position.quantity
      : (isPositiveNumber(position?.value) ? position.value : 0);

    const value = typeof rawValue === 'number' && !Number.isNaN(rawValue) ? rawValue : 0;
    totalValue += value;
    aggregate.set(assetId, (aggregate.get(assetId) || 0) + value);
  });

  return Array.from(aggregate.entries())
    .map(([assetId, value]) => ({
      assetId,
      value,
      percent: totalValue > 0 ? (value / totalValue) * 100 : 0,
    }))
    .sort((left, right) => right.value - left.value);
}

function derivePortfolioProfile(exposure, top3Concentration) {
  if (exposure.stable >= 0.45) {
    return 'Défensif';
  }
  if (top3Concentration >= 0.78 || exposure.btc >= 0.65 || exposure.alt >= 0.65) {
    return 'Concentré';
  }
  if (exposure.stable < 0.12 && exposure.alt >= 0.35) {
    return 'Offensif';
  }
  return 'Équilibré';
}

function deriveAlignmentScore({ exposure, top3Concentration, marketRegime, marketBtcDominance, marketEthDominance }) {
  const normalizedRegime = String(marketRegime || '').toUpperCase();
  let score = 0.66;

  if (normalizedRegime.includes('BTC')) {
    const targetBtc = typeof marketBtcDominance === 'number'
      ? clamp(marketBtcDominance / 100, 0.4, 0.75)
      : 0.58;
    score = 1 - Math.abs(exposure.btc - targetBtc) * 1.35;
    score -= Math.max(0, exposure.alt - 0.32) * 0.55;
  } else if (normalizedRegime.includes('ALT')) {
    score = 1 - Math.abs(exposure.alt - 0.42) * 1.25;
    score -= Math.max(0, exposure.btc - 0.45) * 0.75;
    score -= Math.abs(exposure.stable - 0.12) * 0.35;
  } else if (normalizedRegime.includes('ETH')) {
    const targetEth = typeof marketEthDominance === 'number'
      ? clamp(marketEthDominance / 100, 0.18, 0.34)
      : 0.24;
    score = 1 - Math.abs(exposure.eth - targetEth) * 1.45;
    score -= Math.max(0, exposure.btc - 0.48) * 0.45;
    score -= Math.abs(exposure.stable - 0.12) * 0.25;
  }

  score -= Math.max(0, top3Concentration - 0.72) * 0.45;
  return clamp(score, 0.18, 0.96);
}

function buildAlignmentTone(score, marketRegimeLabel) {
  if (score >= 0.78) {
    return {
      headline: 'Bien positionné pour le régime actuel',
      helper: `La structure du portefeuille reste cohérente avec ${marketRegimeLabel}.`,
      color: '#1f7a4d',
      background: 'rgba(31,122,77,0.12)',
      progress: '#2a8f5a',
    };
  }

  if (score >= 0.58) {
    return {
      headline: `Alignement intermédiaire avec ${marketRegimeLabel}`,
      helper: 'Le portefeuille peut encore être optimisé sans être en décalage majeur.',
      color: '#946b13',
      background: 'rgba(148,107,19,0.12)',
      progress: '#d89b1f',
    };
  }

  return {
    headline: `Faiblement aligné avec ${marketRegimeLabel}`,
    helper: 'Le portefeuille risque de manquer la phase dominante si le régime se confirme.',
    color: '#a43e2a',
    background: 'rgba(164,62,42,0.12)',
    progress: '#d45f43',
  };
}

function buildEmptyAlignmentTone() {
  return {
    headline: 'Positionnement en attente de données',
    helper: 'Connecte tes comptes ou ajoute une position pour activer l’évaluation d’alignement.',
    color: '#23546e',
    background: 'rgba(35,84,110,0.12)',
    progress: '#23546e',
  };
}

function deriveTopRisk({
  totalValue,
  exposure,
  top3Concentration,
  assetCount,
  marketRegime,
  marketRegimeLabel,
  marketExplanation,
  marketBtcDominance,
}) {
  const normalizedRegime = String(marketRegime || '').toUpperCase();
  const fallbackContext = marketExplanation || `Le contexte actuel pointe plutôt vers ${marketRegimeLabel}.`;

  if (totalValue <= 0) {
    return {
      severity: 'info',
      title: 'Portefeuille en attente de données',
      message: 'Synchronise tes comptes ou ajoute tes positions pour activer la lecture stratégique.',
      why: 'Aucune position valorisée n’est encore consolidée dans ce portefeuille.',
      marketContext: fallbackContext,
      impact: 'Aucun risque dominant ne peut être calculé tant que le portefeuille est vide.',
    };
  }

  if (exposure.btc >= 0.65 && (!normalizedRegime.includes('BTC') || (typeof marketBtcDominance === 'number' && marketBtcDominance < 52))) {
    return {
      severity: 'warning',
      title: 'Portefeuille fortement concentré en BTC',
      message: 'Risque élevé en cas de rotation vers les altcoins.',
      why: `Votre portefeuille est composé à ${formatShare(exposure.btc)} de BTC.`,
      marketContext: fallbackContext,
      impact: 'Sous-performance possible si la rotation hors BTC se confirme.',
    };
  }

  if (top3Concentration >= 0.78) {
    return {
      severity: 'warning',
      title: 'Concentration trop forte sur quelques lignes',
      message: 'Le portefeuille dépend d’un nombre trop limité d’actifs.',
      why: `Les 3 premières lignes représentent ${formatShare(top3Concentration)} du portefeuille.`,
      marketContext: fallbackContext,
      impact: 'Un choc sur un actif majeur peut dégrader rapidement la performance globale.',
    };
  }

  if (exposure.alt >= 0.6 && normalizedRegime.includes('BTC')) {
    return {
      severity: 'warning',
      title: 'Portefeuille trop exposé aux altcoins',
      message: 'Le régime actuel favorise davantage les actifs dominants.',
      why: `L’exposition altcoins atteint ${formatShare(exposure.alt)} du portefeuille.`,
      marketContext: fallbackContext,
      impact: 'Le portefeuille peut sous-performer si la dominance BTC reste forte.',
    };
  }

  if (exposure.stable <= 0.05) {
    return {
      severity: 'warning',
      title: 'Poche défensive trop faible',
      message: 'Le portefeuille a peu d’amortisseur en cas de correction rapide.',
      why: `Les stablecoins ne représentent que ${formatShare(exposure.stable)} du portefeuille.`,
      marketContext: fallbackContext,
      impact: 'Le drawdown peut être plus brutal en cas de retournement ou de volatilité élevée.',
    };
  }

  return {
    severity: 'success',
    title: 'Risque principal sous contrôle',
    message: assetCount <= 2
      ? 'La structure reste simple, mais aucun drapeau critique ne domine à ce stade.'
      : 'Aucun risque dominant ne ressort clairement par rapport au régime suivi.',
    why: 'La concentration BTC, la poche stable et la diversification restent dans des zones exploitées sans alerte majeure.',
    marketContext: fallbackContext,
    impact: 'La marge d’optimisation existe, mais le portefeuille n’affiche pas de vulnérabilité critique immédiate.',
  };
}

function scrollToSection(sectionId) {
  if (typeof document === 'undefined') {
    return;
  }

  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function resolveRiskActionSection(actionLabel) {
  const normalized = String(actionLabel || '').toLowerCase();

  if (normalized.includes('opportun')) {
    return 'portfolio-opportunities-section';
  }
  return 'portfolio-macro-section';
}

function isRebalanceAction(actionLabel) {
  const normalized = String(actionLabel || '').toLowerCase();
  return normalized.includes('rééquilibr') || normalized.includes('reequilibr');
}

const UnifiedPortfolioPage = () => {
  const { totalValue, totalAssets, connectedExchanges, change24hValue, change24hPercent, lastSyncTime } = usePortfolioHeaderSelector();
  const allocation = usePortfolioStore((state) => state.allocation);
  const getUnifiedPositions = usePortfolioStore((state) => state.getUnifiedPositions);
  const portfolioInsights = usePortfolioStore((state) => state.insights);
  const setMacro = useMacroMarketStore((state) => state.setState);
  const macro = useMacroMarketStore();
  const [portfolioOverview, setPortfolioOverview] = useState(null);
  const [overviewAvailable, setOverviewAvailable] = useState(false);
  const [riskDrawerOpen, setRiskDrawerOpen] = useState(false);

  const allocationEntries = useMemo(() => {
    if (Array.isArray(allocation) && allocation.length > 0) {
      return allocation
        .map((entry) => ({
          assetId: typeof entry?.assetId === 'string' ? entry.assetId.toUpperCase() : '',
          percent: typeof entry?.percent === 'number' ? entry.percent : 0,
          value: typeof totalValue === 'number' ? ((entry?.percent || 0) / 100) * totalValue : 0,
        }))
        .filter((entry) => entry.assetId)
        .sort((left, right) => right.percent - left.percent);
    }

    const resolvedPositions = typeof getUnifiedPositions === 'function' ? getUnifiedPositions() : [];
    return buildAllocationFromPositions(resolvedPositions);
  }, [allocation, totalValue, getUnifiedPositions]);

  const exposure = useMemo(() => {
    const btc = allocationEntries.find((entry) => entry.assetId === 'BTC')?.percent || 0;
    const eth = allocationEntries.find((entry) => entry.assetId === 'ETH')?.percent || 0;
    const stable = allocationEntries
      .filter((entry) => STABLECOIN_SYMBOLS.includes(entry.assetId))
      .reduce((sum, entry) => sum + entry.percent, 0);
    const alt = Math.max(0, 100 - btc - eth - stable);

    return {
      btc: btc / 100,
      eth: eth / 100,
      stable: stable / 100,
      alt: alt / 100,
    };
  }, [allocationEntries]);

  const top3Concentration = useMemo(
    () => allocationEntries.slice(0, 3).reduce((sum, entry) => sum + (entry.percent || 0), 0) / 100,
    [allocationEntries],
  );

  const overviewExposure = useMemo(() => {
    const normalizedBackendExposure = normalizeExposureSnapshot(portfolioOverview?.exposure);
    if (!normalizedBackendExposure) {
      return exposure;
    }

    const backendExposureTotal = sumExposureValues(normalizedBackendExposure);
    const localExposureTotal = sumExposureValues(exposure);

    if (backendExposureTotal <= 0.001 && localExposureTotal > 0.001) {
      return exposure;
    }

    return normalizedBackendExposure;
  }, [exposure, portfolioOverview?.exposure]);

  const fallbackPortfolioProfile = useMemo(() => derivePortfolioProfile(exposure, top3Concentration), [exposure, top3Concentration]);
  const fallbackMarketRegime = deriveMarketRegime(macro.marketRegime, macro.btcMarketDominance, macro.marketEthDominance);
  const hasBackendMarket = Boolean(portfolioOverview?.market);
  const marketRegime = hasBackendMarket ? (portfolioOverview.market.regime || 'NEUTRAL') : fallbackMarketRegime;
  const marketRegimeLabel = portfolioOverview?.market?.regimeLabel || humanizeMarketRegime(marketRegime);
  const backendAssetCount = coerceNumber(portfolioOverview?.assetCount);
  const effectiveAssetCount = backendAssetCount !== null ? backendAssetCount : (allocationEntries.length || totalAssets || 0);
  const backendPortfolioValue = coerceNumber(portfolioOverview?.portfolioValue);
  const effectivePortfolioValue = (typeof totalValue === 'number' && totalValue > 0)
    ? totalValue
    : (backendPortfolioValue ?? 0);
  const isEmptyPortfolio = effectiveAssetCount === 0 || ((effectivePortfolioValue ?? 0) <= 0);
  const alignmentScore = useMemo(() => {
    if (isEmptyPortfolio) {
      return 0;
    }

    const backendAlignmentScore = coerceNumber(portfolioOverview?.alignmentScore);
    if (backendAlignmentScore !== null) {
      return backendAlignmentScore;
    }

    return deriveAlignmentScore({
      exposure,
      top3Concentration,
      marketRegime,
      marketBtcDominance: macro.btcMarketDominance,
      marketEthDominance: macro.marketEthDominance,
    });
  }, [exposure, isEmptyPortfolio, macro.btcMarketDominance, macro.marketEthDominance, marketRegime, portfolioOverview, top3Concentration]);
  const alignmentTone = useMemo(
    () => (isEmptyPortfolio ? buildEmptyAlignmentTone() : buildAlignmentTone(alignmentScore, marketRegimeLabel)),
    [alignmentScore, isEmptyPortfolio, marketRegimeLabel],
  );
  const portfolioProfile = isEmptyPortfolio ? 'En attente' : (portfolioOverview?.portfolioProfile || fallbackPortfolioProfile);
  const topRisk = useMemo(() => {
    if (isEmptyPortfolio) {
      return {
        severity: 'info',
        title: 'Portefeuille en attente de données',
        message: 'Synchronisez vos comptes ou ajoutez des positions pour activer la lecture stratégique.',
        why: 'Aucune position valorisée n’est encore consolidée dans cette vue portefeuille.',
        impact: 'Le hero affichera automatiquement le vrai risque dominant dès qu’un portefeuille exploitable sera détecté.',
        marketContext: portfolioOverview?.market?.explanation || macro.marketRegimeExplanation || `Régime suivi: ${marketRegimeLabel}.`,
        actions: ['Voir opportunités compatibles', 'Analyser exposition'],
      };
    }

    if (portfolioOverview?.topRisk) {
      return portfolioOverview.topRisk;
    }

    return deriveTopRisk({
      totalValue,
      exposure,
      top3Concentration,
      assetCount: effectiveAssetCount,
      marketRegime,
      marketRegimeLabel,
      marketExplanation: macro.marketRegimeExplanation,
      marketBtcDominance: macro.btcMarketDominance,
    });
  }, [effectiveAssetCount, exposure, isEmptyPortfolio, macro.btcMarketDominance, macro.marketRegimeExplanation, marketRegime, marketRegimeLabel, portfolioOverview, top3Concentration, totalValue]);

  useEffect(() => {
    let active = true;

    async function loadPortfolioOverview() {
      try {
        const response = await fetch('/api/portfolio');
        if (!response.ok) {
          throw new Error('Vue portefeuille indisponible');
        }

        const payload = await response.json();
        if (active) {
          setPortfolioOverview(payload);
          setOverviewAvailable(true);

          const nextMacroState = {};
          if (payload?.market?.regime) {
            nextMacroState.marketRegime = payload.market.regime;
          }
          if (payload?.market?.explanation) {
            nextMacroState.marketRegimeExplanation = payload.market.explanation;
          }
          const btcDominance = coerceNumber(payload?.market?.btcDominance);
          const ethDominance = coerceNumber(payload?.market?.ethDominance);
          if (btcDominance !== null) {
            nextMacroState.btcMarketDominance = btcDominance;
          }
          if (ethDominance !== null) {
            nextMacroState.marketEthDominance = ethDominance;
          }
          if (Object.keys(nextMacroState).length > 0) {
            setMacro(nextMacroState);
          }
        }
      } catch (error) {
        if (active) {
          setPortfolioOverview(null);
          setOverviewAvailable(false);
        }
      }
    }

    loadPortfolioOverview();
    return () => {
      active = false;
    };
  }, [lastSyncTime, setMacro, totalValue]);

  const todayPnl = isEmptyPortfolio ? null : (coerceNumber(portfolioOverview?.performance?.day?.pnl) ?? change24hValue);
  const todayPercent = isEmptyPortfolio ? null : (coerceNumber(portfolioOverview?.performance?.day?.percent) ?? change24hPercent);
  const performanceRows = isEmptyPortfolio
    ? [
      { label: '7j', percent: null },
      { label: '30j', percent: null },
      { label: 'All time', percent: null },
    ]
    : [
      { label: '7j', percent: coerceNumber(portfolioOverview?.performance?.week?.percent) },
      { label: '30j', percent: coerceNumber(portfolioOverview?.performance?.month?.percent) },
      { label: 'All time', percent: coerceNumber(portfolioOverview?.performance?.allTime?.percent) },
    ];

  const exposureSegments = [
    { label: 'BTC', value: overviewExposure.btc, color: '#f3c65c' },
    { label: 'ETH', value: overviewExposure.eth, color: '#7dd0d3' },
    { label: 'ALT', value: overviewExposure.alt, color: '#6ea85f' },
    { label: 'Stable', value: overviewExposure.stable, color: '#d9dfeb' },
  ];

  const topRiskActions = (Array.isArray(topRisk?.actions) && topRisk.actions.length > 0
    ? topRisk.actions
    : ['Voir opportunités compatibles', 'Analyser exposition'])
    .filter((actionLabel) => !isRebalanceAction(actionLabel))
    .slice(0, 3);

  const riskPalette = topRisk.severity === 'success'
    ? { background: 'rgba(31,122,77,0.12)', border: 'rgba(31,122,77,0.24)', color: '#1f7a4d' }
    : topRisk.severity === 'info'
      ? { background: 'rgba(35,84,110,0.12)', border: 'rgba(35,84,110,0.24)', color: '#23546e' }
      : { background: 'rgba(164,62,42,0.12)', border: 'rgba(164,62,42,0.24)', color: '#a43e2a' };

  const performanceTone = isEmptyPortfolio
    ? { color: '#23546e', background: 'rgba(35,84,110,0.12)' }
    : (typeof todayPercent === 'number' && todayPercent >= 0
      ? { color: '#1f7a4d', background: 'rgba(31,122,77,0.12)' }
      : { color: '#a43e2a', background: 'rgba(164,62,42,0.12)' });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top left, rgba(228,197,114,0.18) 0%, rgba(228,197,114,0) 28%), linear-gradient(180deg, #f8f4ec 0%, #f2ede3 52%, #ebe5db 100%)',
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 6,
            p: { xs: 3, md: 4.5 },
            mb: 4,
            background: 'linear-gradient(135deg, #10252b 0%, #214550 36%, #46756f 100%)',
            color: '#f8f4ec',
            boxShadow: '0 24px 60px rgba(16,37,43,0.18)',
          }}
        >
          <Stack spacing={3}>
            <Box sx={{ maxWidth: 860 }}>
              <Chip
                label="Positionnement du portefeuille"
                sx={{
                  mb: 2,
                  bgcolor: 'rgba(248,244,236,0.14)',
                  color: '#f8f4ec',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, maxWidth: 860 }}>
                Voici votre situation stratégique actuelle, pas seulement la valeur du portefeuille
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(248,244,236,0.82)', maxWidth: 760, lineHeight: 1.8 }}>
                Le hero répond d’abord à la question “suis-je bien positionné ?” en combinant valeur, performance,
                alignement marché, risque principal, structure d’exposition et actions prioritaires.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} flexWrap="wrap" useFlexGap>
              <Chip label={`Profil ${portfolioProfile}`} sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
              <Chip label={`Régime ${marketRegimeLabel}`} sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
              <Chip label={`${effectiveAssetCount} actifs suivis`} sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
              <Chip label={`${connectedExchanges} plateformes connectées`} sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
              <Chip label={overviewAvailable ? 'Hero backend unifié' : 'Fallback local'} sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
              <Chip label={`Dernière sync ${formatSyncDate(lastSyncTime)}`} sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  gridColumn: { lg: 'span 7' },
                  p: 3,
                  borderRadius: 5,
                  background: 'rgba(248,244,236,0.1)',
                  border: '1px solid rgba(248,244,236,0.14)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography variant="overline" sx={{ color: 'rgba(248,244,236,0.72)', letterSpacing: '0.12em', fontWeight: 800 }}>
                  Valeur + performance
                </Typography>
                <Typography variant="h2" sx={{ mt: 0.7, fontWeight: 900, lineHeight: 1 }}>
                  {formatUsd(effectivePortfolioValue)}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      px: 1.6,
                      py: 0.9,
                      borderRadius: 99,
                      bgcolor: performanceTone.background,
                      color: performanceTone.color,
                      fontWeight: 800,
                    }}
                  >
                    {isEmptyPortfolio
                      ? 'Valorisation en attente'
                      : `${formatSignedUsd(todayPnl)} (${formatSignedPercent(todayPercent)}) aujourd’hui`}
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.8 }}>
                    {isEmptyPortfolio
                      ? 'Le portefeuille ne contient pas encore de positions valorisées, le hero restera en mode guidage jusqu’à la première sync exploitable.'
                      : 'La valeur seule ne suffit pas: la dynamique récente doit être visible immédiatement.'}
                  </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} flexWrap="wrap" useFlexGap sx={{ mt: 2.2 }}>
                  {performanceRows.map((row) => (
                    <Chip
                      key={row.label}
                      label={`${row.label} ${row.percent !== null ? formatSignedPercent(row.percent) : 'n/a'}`}
                      sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                    />
                  ))}
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  gridColumn: { lg: 'span 5' },
                  p: 3,
                  borderRadius: 5,
                  background: riskPalette.background,
                  border: `1px solid ${riskPalette.border}`,
                  color: '#f8f4ec',
                }}
              >
                <Typography variant="overline" sx={{ color: 'rgba(248,244,236,0.72)', letterSpacing: '0.12em', fontWeight: 800 }}>
                  Risque principal
                </Typography>
                <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ mt: 1.2 }}>
                  <WarningAmberRoundedIcon sx={{ color: riskPalette.color, mt: 0.2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.6, color: '#f8f4ec' }}>
                      {topRisk.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(248,244,236,0.82)', lineHeight: 1.75 }}>
                      {topRisk.message}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="outlined"
                  onClick={() => setRiskDrawerOpen(true)}
                  sx={{
                    mt: 2,
                    borderRadius: 99,
                    fontWeight: 700,
                    color: '#f8f4ec',
                    borderColor: 'rgba(248,244,236,0.28)',
                  }}
                >
                  Voir pourquoi
                </Button>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  gridColumn: { lg: 'span 5' },
                  p: 3,
                  borderRadius: 5,
                  background: alignmentTone.background,
                  border: '1px solid rgba(248,244,236,0.14)',
                }}
              >
                <Typography variant="overline" sx={{ color: 'rgba(248,244,236,0.72)', letterSpacing: '0.12em', fontWeight: 800 }}>
                  Score d’alignement
                </Typography>
                <Typography variant="h2" sx={{ mt: 0.7, fontWeight: 900, lineHeight: 1, color: '#f8f4ec' }}>
                  {isEmptyPortfolio ? 'En attente' : `${Math.round(alignmentScore * 100)}/100`}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1.2, color: '#f8f4ec', fontWeight: 700, lineHeight: 1.7 }}>
                  {alignmentTone.headline}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.8, color: 'rgba(248,244,236,0.8)', lineHeight: 1.75 }}>
                  {alignmentTone.helper}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={alignmentScore * 100}
                  sx={{
                    mt: 2,
                    height: 10,
                    borderRadius: 999,
                    bgcolor: 'rgba(248,244,236,0.12)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: alignmentTone.progress,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1.1, color: 'rgba(248,244,236,0.72)', lineHeight: 1.7 }}>
                  {portfolioOverview?.market?.explanation || macro.marketRegimeExplanation || `Régime suivi: ${marketRegimeLabel}.`}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  gridColumn: { lg: 'span 7' },
                  p: 3,
                  borderRadius: 5,
                  background: 'rgba(248,244,236,0.1)',
                  border: '1px solid rgba(248,244,236,0.14)',
                }}
              >
                <Typography variant="overline" sx={{ color: 'rgba(248,244,236,0.72)', letterSpacing: '0.12em', fontWeight: 800 }}>
                  Résumé d’exposition
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} flexWrap="wrap" useFlexGap sx={{ mt: 1.4 }}>
                  {exposureSegments.map((segment) => (
                    <Chip
                      key={segment.label}
                      label={`${segment.label} ${formatShare(segment.value)}`}
                      sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                    />
                  ))}
                </Stack>
                <Box
                  sx={{
                    mt: 2,
                    height: 16,
                    display: 'flex',
                    overflow: 'hidden',
                    borderRadius: 999,
                    bgcolor: 'rgba(248,244,236,0.12)',
                    border: '1px solid rgba(248,244,236,0.08)',
                  }}
                >
                  {exposureSegments.map((segment) => (
                    <Box
                      key={segment.label}
                      sx={{
                        width: `${segment.value * 100}%`,
                        minWidth: segment.value > 0 ? 10 : 0,
                        bgcolor: segment.color,
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="body2" sx={{ mt: 1.4, color: 'rgba(248,244,236,0.78)', lineHeight: 1.75 }}>
                  Dominante actuelle: {allocationEntries[0]?.assetId || 'n/a'} • Top 3 lignes: {formatShare(top3Concentration)} •
                  Insights actifs: {Array.isArray(portfolioInsights) ? portfolioInsights.length : 0}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  gridColumn: '1 / -1',
                  p: 3,
                  borderRadius: 5,
                  background: 'rgba(248,244,236,0.08)',
                  border: '1px solid rgba(248,244,236,0.14)',
                }}
              >
                <Typography variant="overline" sx={{ color: 'rgba(248,244,236,0.72)', letterSpacing: '0.12em', fontWeight: 800 }}>
                  Actions prioritaires
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<InsightsRoundedIcon />}
                    onClick={() => scrollToSection('portfolio-opportunities-section')}
                    sx={{
                      borderRadius: 99,
                      px: 2.2,
                      py: 1.2,
                      bgcolor: '#f3c65c',
                      color: '#10252b',
                      fontWeight: 800,
                      ':hover': { bgcolor: '#f0bc41' },
                    }}
                  >
                    Voir opportunités compatibles
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DonutLargeRoundedIcon />}
                    onClick={() => setRiskDrawerOpen(true)}
                    sx={{ borderRadius: 99, px: 2.2, py: 1.2, color: '#f8f4ec', borderColor: 'rgba(248,244,236,0.28)', fontWeight: 700 }}
                  >
                    Analyser exposition
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        </Paper>

        <UnifiedPortfolioApp />
      </Container>

      <Drawer
        anchor="right"
        open={riskDrawerOpen}
        onClose={() => setRiskDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            p: 3,
            background: 'linear-gradient(180deg, #f8f4ec 0%, #f1eadf 100%)',
          },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em', fontWeight: 800 }}>
              Détail du risque prioritaire
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c', mt: 0.8 }}>
              {topRisk.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#556970', lineHeight: 1.8 }}>
              {topRisk.message}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 0.6 }}>
              Pourquoi
            </Typography>
            <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.8 }}>
              {topRisk.why}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 0.6 }}>
              Contexte marché
            </Typography>
            <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.8 }}>
              {topRisk.marketContext}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 0.6 }}>
              Impact
            </Typography>
            <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.8 }}>
              {topRisk.impact}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 0.8 }}>
              Actions
            </Typography>
            <Stack spacing={1}>
                  {topRiskActions.map((actionLabel, index) => (
                    <Button
                      key={actionLabel}
                      variant={index === 0 ? 'contained' : index === 1 ? 'outlined' : 'text'}
                      onClick={() => {
                        setRiskDrawerOpen(false);
                        scrollToSection(resolveRiskActionSection(actionLabel));
                      }}
                      sx={index === 0 ? { borderRadius: 99, bgcolor: '#17323c', ':hover': { bgcolor: '#10252b' } } : { borderRadius: 99 }}
                    >
                      {actionLabel}
                    </Button>
                  ))}
            </Stack>
          </Box>
        </Stack>
      </Drawer>
    </Box>
  );
};

export default UnifiedPortfolioPage;
